import { UseFormValues } from "@formiz/core/dist/types/form.types";
import { useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconSize,
  IconVariants,
  Input,
  Text,
  TextArea,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";

import { FormField, SpicyButton } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { APPLICATIONS_ID, PERSONA_FIELD_LIMITS, USER_ROLES } from "@/constants";
import AvatarCreate from "@/pages/CreateCharacter/v2/components/AvatarCreate";
import { getIsPartner } from "@/utils/getIsPartner";

import { ProfileFormFields } from "../../types";
import { getProfileFormFieldSchema } from "../../utils";
import getProfileHighlightsPlaceholder from "../../utils/getProfileHighglightsPlaceholder";
import CurrentPlan from "./CurrentPlan";

interface IUserProfileInfoProps {
  profileForm: UseFormValues;
  setFieldsValues: (values: Record<string, unknown>) => void;
  isLoadingUpdate: boolean;
  isCheckingUsername: boolean;
  isUsernameAvailable: boolean | null;
  errorMessage: string | null;
  debouncedUsername: string;
}

const UserProfileInfo = ({
  profileForm,
  setFieldsValues,
  isLoadingUpdate,
  isCheckingUsername,
  isUsernameAvailable,
  errorMessage,
  debouncedUsername,
}: IUserProfileInfoProps) => {
  const { t } = useTranslation();
  const isUploadAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ProfileUploadAvatar);
  const applicationId = basicAppConfig.applicationId;
  const { roles, isProfileModified: hasChangesPending, role: subscriptionRole } = useUserInfo();
  const isBetaUser = roles?.includes(USER_ROLES.BETA_USER);
  const isPartner = getIsPartner();
  const highlightsPlaceholder = getProfileHighlightsPlaceholder(applicationId as APPLICATIONS_ID, t);
  const isSaveDisabled = !hasChangesPending || isCheckingUsername || !profileForm?.isValid || debouncedUsername !== profileForm?.values?.username;

  return (
    <div className="flex flex-col gap-lg py-lg">
      <FormField
        as="input"
        name="username"
        label={t("user:profile.form.username.label")}
        toLowerCase={true}
        required
        validations={getProfileFormFieldSchema(ProfileFormFields.USERNAME, t).validations}
        showError={isUsernameAvailable === false}
      >
        <Input
          placeholder={t("user:profile.form.username.placeholder")}
          helperText={t("user:profile.form.username.helperText")}
          maxLength={getProfileFormFieldSchema(ProfileFormFields.USERNAME, t).maxLength}
          aria-labelledby="username"
          loading={isCheckingUsername}
          iconEnd={isUsernameAvailable ? IconVariants.CHECK_CIRCLE : undefined}
          classNames={{
            iconEnd: "!text-green-9",
          }}
        />
      </FormField>

      <FormField
        as="input"
        name="name"
        label={t("user:profile.form.name.label")}
        required
        validations={getProfileFormFieldSchema(ProfileFormFields.NAME, t).validations}
      >
        <Input
          placeholder={t("user:profile.form.name.placeholder")}
          helperText={t("user:profile.form.name.helperText")}
          maxLength={getProfileFormFieldSchema(ProfileFormFields.NAME, t).maxLength}
          aria-labelledby="name"
        />
      </FormField>

      <div className="flex items-center gap-2">
        <div className="grow">
          {!!(isPartner && subscriptionRole === USER_ROLES.PREMIUM) && (
            <div className="flex flex-col gap-sm">
              <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
                <Trans i18nKey="user:profile.currentPlan.title" />
              </Text>
              <SpicyButton
                variant="tertiary_short"
                color="gradient_secondary"
                endContent={<Icon icon={IconVariants.GEM} className="ml-1.5" size={IconSize.MEDIUM} />}
              >
                <Text>
                  <Trans i18nKey="user:profile.currentPlan.premium" />
                </Text>
              </SpicyButton>
            </div>
          )}

          {!isPartner && <CurrentPlan />}
        </div>

        {!!isBetaUser && (
          <span className="inline px-2 py-0.5 mt-2 rounded-full text-xs bg-purple-100 text-purple-700 font-medium">
            <Trans i18nKey="user:profile.currentPlan.betaUser" />
          </span>
        )}
      </div>

      <FormField as="custom" name="avatar_url" label={t("user:profile.form.avatar.label")} description={t("user:profile.form.avatar.description")}>
        <AvatarCreate
          characterAvatar={profileForm.values.avatar_url}
          setCharacterAvatar={(value: string) => setFieldsValues({ avatar_url: value })}
          disabled={false}
          classNames={{ image: "!size-[150px]" }}
          isAiPromptEnabled={false}
          isUploadAllowed={isUploadAllowed}
        />
      </FormField>

      <FormField
        as="input"
        name="highlights"
        label={t("user:profile.form.highlights.label")}
        validations={getProfileFormFieldSchema(ProfileFormFields.HIGHLIGHTS, t).validations}
      >
        <TextArea
          placeholder={highlightsPlaceholder}
          helperText={t("user:profile.form.highlights.helperText")}
          maxLength={PERSONA_FIELD_LIMITS.HIGHLIGHT}
          aria-labelledby="name"
          rows={2}
        />
      </FormField>

      {errorMessage ? (
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-9">
          {errorMessage}
        </Text>
      ) : null}

      <Button
        variant={ButtonVariant.INVERSE}
        size={ButtonSize.LARGE}
        disabled={isSaveDisabled}
        loading={isLoadingUpdate}
        type="submit"
        className="w-max"
      >
        <Trans i18nKey="user:profile.form.button.save" />
      </Button>
    </div>
  );
};

export default UserProfileInfo;
